// LIDO-aware normalizer (no frameworks)
// Works with: <lido:lidoWrap><lido:lido>…</lido:lido>…</lido:lidoWrap>

const NS = {
  lido: "http://www.lido-schema.org",
  xsi: "http://www.w3.org/2001/XMLSchema-instance",
};

const A = Array.from;

/** Helpers that respect namespaces */
function $allNS(node, localName, ns = NS.lido) {
  return A(node.getElementsByTagNameNS(ns, localName));
}
function $oneNS(node, localName, ns = NS.lido) {
  return node.getElementsByTagNameNS(ns, localName)[0] || null;
}
function txt(node) {
  return (node?.textContent || "").trim();
}

/** Pick first non-empty string */
function first(...vals) {
  return vals.find((v) => v && v.length) || "";
}

/** Extract display date or earliest/latest range as a string */
function extractDate(lidoEl) {
  const eventWrap = $oneNS(lidoEl, "eventWrap");
  if (!eventWrap) return "";
  const display = $oneNS(eventWrap, "displayDate");
  if (display) return txt(display);

  // fallback: earliest/latest
  const earliest = $oneNS(eventWrap, "earliestDate");
  const latest = $oneNS(eventWrap, "latestDate");
  const e = txt(earliest),
    l = txt(latest);
  if (e && l && e !== l) return `${e}–${l}`;
  return e || l || "";
}

/** Extract categories from objectWorkType terms (and optionally recordType term) */
function extractCategories(lidoEl) {
  const cats = new Set();

  const owtWrap = $oneNS(
    $oneNS(
      $oneNS(lidoEl, "objectIdentificationWrap") || lidoEl,
      "objectClassificationWrap"
    ) || lidoEl,
    "objectWorkTypeWrap"
  );
  if (owtWrap) {
    for (const owt of $allNS(owtWrap, "objectWorkType")) {
      for (const term of $allNS(owt, "term")) cats.add(txt(term));
    }
  }

  const admin = $oneNS(lidoEl, "administrativeMetadata");
  if (admin) {
    const recWrap = $oneNS(admin, "recordWrap");
    const recType = recWrap && $oneNS(recWrap, "recordType");
    if (recType)
      for (const term of $allNS(recType, "term")) {
        const s = txt(term);
        if (s) cats.add(s);
      }
  }

  return [...cats].filter(Boolean);
}

/** Extract a summary / descriptive text */
function extractSummary(lidoEl) {
  const idWrap = $oneNS(lidoEl, "objectIdentificationWrap");
  const descWrap = idWrap && $oneNS(idWrap, "objectDescriptionWrap");
  const descSet = descWrap && $oneNS(descWrap, "objectDescriptionSet");
  const note = descSet && $oneNS(descSet, "descriptiveNoteValue");
  return txt(note);
}

/** Extract title (prefer lido:appellationValue with pref='preferred') */
function extractTitle(lidoEl) {
  const idWrap = $oneNS(lidoEl, "objectIdentificationWrap");
  const titleWrap = idWrap && $oneNS(idWrap, "titleWrap");
  if (!titleWrap) return "";

  const titleSets = $allNS(titleWrap, "titleSet");
  // try preferred first
  for (const set of titleSets) {
    for (const app of $allNS(set, "appellationValue")) {
      if (app.getAttributeNS(NS.lido, "pref") === "preferred") return txt(app);
    }
  }
  // fallback: first available
  for (const set of titleSets) {
    const app = $oneNS(set, "appellationValue");
    if (app) return txt(app);
  }
  return "";
}

/** Extract record IDs (stable internal + external) */
function extractIDs(lidoEl) {
  const recID = $oneNS(lidoEl, "lidoRecID");
  const admin = $oneNS(lidoEl, "administrativeMetadata");
  const recordWrap = admin && $oneNS(admin, "recordWrap");
  const recordID = recordWrap && $oneNS(recordWrap, "recordID");

  return {
    lidoRecID: txt(recID),
    recordID: txt(recordID),
    id: first(txt(recordID), txt(recID)), // main ID for UI
  };
}

/** Extract repository/location & inventory number (useful for detail view) */
function extractRepository(lidoEl) {
  const idWrap = $oneNS(lidoEl, "objectIdentificationWrap");
  const repoWrap = idWrap && $oneNS(idWrap, "repositoryWrap");
  const repoSet = repoWrap && $oneNS(repoWrap, "repositorySet");
  if (!repoSet) return {};

  const repoName = $oneNS(repoSet, "repositoryName");
  const legalBodyName = repoName && $oneNS(repoName, "legalBodyName");
  const repoLabel = legalBodyName && $oneNS(legalBodyName, "appellationValue");

  const workID = $oneNS(repoSet, "workID");
  const repoLoc = $oneNS(repoSet, "repositoryLocation");
  const namePlaceSet = repoLoc && $oneNS(repoLoc, "namePlaceSet");
  const placeVal = namePlaceSet && $oneNS(namePlaceSet, "appellationValue");

  return {
    repository: txt(repoLabel),
    inventoryNumber: txt(workID),
    repositoryLocation: txt(placeVal),
  };
}

/** Extract image list from administrativeMetadata/resourceWrap/resourceSet/linkResource
 * Extract only image resources (ignore notation_incipit / MEI / PAE)
 */
function extractImages(lidoEl, baseHref = "") {
  const NS = "http://www.lido-schema.org";
  const A = (n, q) => Array.from(n.getElementsByTagNameNS(NS, q));
  const one = (n, q) => n.getElementsByTagNameNS(NS, q)[0] || null;
  const T = (n) => (n?.textContent || "").trim();

  // safer attr getter (handles lido:type and unprefixed)
  const attr = (el, name) =>
    (
      el?.getAttributeNS(NS, name) ||
      el?.getAttribute(`lido:${name}`) ||
      el?.getAttribute(name) ||
      ""
    ).trim();

  const isImageHref = (href = "") => /\.(jpe?g|png|gif|webp|svg)$/i.test(href);
  const images = [];

  const admin = one(lidoEl, "administrativeMetadata");
  const rwrap = admin && one(admin, "resourceWrap");
  if (!rwrap) return images;

  for (const set of A(rwrap, "resourceSet")) {
    for (const rep of A(set, "resourceRepresentation")) {
      const typeAttr = attr(rep, "type"); // e.g., image_master, notation_incipit
      const link = one(rep, "linkResource");
      const href = T(link);
      const fmtAttr =
        attr(link, "formatResource") || T(one(rep, "formatResource")); // e.g., image/jpeg

      // Decide if this resource is an image
      const isImage =
        (typeAttr && typeAttr.startsWith("image")) ||
        (fmtAttr && fmtAttr.toLowerCase().startsWith("image/")) ||
        isImageHref(href);

      if (!isImage || !href) continue;

      images.push({
        src: resolveSrc(baseHref, href),
        type: typeAttr || (fmtAttr ? "image" : ""), // keep lido:type if present
        alt: "",
      });
    }
  }

  // Sort preference: image_master first, then web/thumb/preview, then others
  const pref = [
    "image_master",
    "image_web",
    "image_thumb",
    "image_preview",
    "",
  ];
  images.sort((a, b) => pref.indexOf(a.type) - pref.indexOf(b.type));
  return images;
}
function extractComposers(lidoEl) {
  const composers = [];
  const events = lidoEl.getElementsByTagNameNS(NS.lido, "event");
  for (const ev of events) {
    const actors = ev.getElementsByTagNameNS(NS.lido, "actorInRole");
    for (const a of actors) {
      const role = a.getElementsByTagNameNS(NS.lido, "roleActor")[0];
      const roleTerm = role && role.getElementsByTagNameNS(NS.lido, "term")[0];
      if (roleTerm && /komponist|composer/i.test(roleTerm.textContent)) {
        const actor = a.getElementsByTagNameNS(NS.lido, "actor")[0];
        const nameSet =
          actor && actor.getElementsByTagNameNS(NS.lido, "nameActorSet")[0];
        const nameVal =
          nameSet &&
          nameSet.getElementsByTagNameNS(NS.lido, "appellationValue")[0];
        if (nameVal) composers.push(nameVal.textContent.trim());
      }
    }
  }
  return composers;
}

function resolveSrc(base, src) {
  try {
    return new URL(src, base || location.origin).toString();
  } catch {
    return src;
  }
}

/** Main: normalize a whole <lido:lidoWrap> or a single <lido:lido> */
export function normalizeLIDO(xmlDoc, { imageBaseHref = "" } = {}) {
  const lidos = $allNS(xmlDoc, "lido"); // finds all <lido:lido> anywhere
  const items = [];

  for (const l of lidos) {
    const { id, lidoRecID, recordID } = extractIDs(l);
    const title = extractTitle(l);
    const dateStr = extractDate(l);
    const summary = extractSummary(l);
    const cats = extractCategories(l);
    const repo = extractRepository(l);
    const composers = extractComposers(l);
    const incipits = extractIncipitsInlineOnly(l);
    const images = extractImages(l, imageBaseHref).map((img) => ({
      ...img,
      alt: img.alt || title,
    }));

    items.push({
      id,
      lidoRecID,
      recordID,
      title,
      date: dateStr, // keep as string for now (1908 / 1908–1910)
      summary,
      categories: cats, // used by your filter UI
      repository: repo.repository || "",
      repositoryLocation: repo.repositoryLocation || "",
      inventoryNumber: repo.inventoryNumber || "",
      media: { images }, // ARRAY of images
      incipit: incipits, // { paeInline }
      composers,
      // a few convenient derived fields:
      yearStart: parseInt((dateStr || "").match(/\d{4}/)?.[0] || "") || null,
      link: extractRecordLink(l),
      tags: extractSubjects(l),
    });
  }
  return items;
}

function extractSubjects(lidoEl) {
  const NS = { lido: "http://www.lido-schema.org" };
  const byName = (n) => lidoEl.getElementsByTagNameNS(NS.lido, n);

  const tags = new Set();

  // displaySubject (free text)
  for (const ds of byName("displaySubject")) {
    const t = (ds.textContent || "").trim();
    if (t) tags.add(t);
  }

  // subjectConcept → term (controlled vocabulary labels)
  const subjectEls = byName("subject");
  for (const s of subjectEls) {
    const concepts = s.getElementsByTagNameNS(NS.lido, "subjectConcept");
    for (const c of concepts) {
      const terms = c.getElementsByTagNameNS(NS.lido, "term");
      for (const term of terms) {
        const lbl = (term.textContent || "").trim();
        if (lbl) tags.add(lbl);
      }
    }
  }
  return [...tags];
}

function extractRecordLink(lidoEl) {
  const admin = $oneNS(lidoEl, "administrativeMetadata");
  const recWrap = admin && $oneNS(admin, "recordWrap");
  const infoSet = recWrap && $oneNS(recWrap, "recordInfoSet");
  const link = infoSet && $oneNS(infoSet, "recordInfoLink");
  return txt(link);
}

function extractIncipitsInlineOnly(lidoEl) {
  const NS = "http://www.lido-schema.org";
  const A = (n, q) => Array.from(n.getElementsByTagNameNS(NS, q));
  const one = (n, q) => n.getElementsByTagNameNS(NS, q)[0] || null;
  const T = (n) => (n?.textContent || "").trim();

  const inc = { paeInline: null };

  const oi = one(lidoEl, "objectIdentificationWrap");
  const odw = oi && one(oi, "objectDescriptionWrap");
  const sets = odw ? A(odw, "objectDescriptionSet") : [];
  for (const s of sets) {
    for (const note of A(s, "descriptiveNoteValue")) {
      // prefer label first (what you’re actually using), then fallback to type
      const label = (
        note.getAttributeNS(NS, "label") ||
        note.getAttribute("lido:label") ||
        note.getAttribute("label") ||
        ""
      ).trim();
      const typeAttr = (
        note.getAttributeNS(NS, "type") ||
        note.getAttribute("lido:type") ||
        note.getAttribute("type") ||
        ""
      ).trim();

      if (label === "incipit-pae" || typeAttr === "incipit-pae") {
        inc.paeInline = T(note);
        return inc;
      }
    }
  }
  return inc;
}
